import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Truck, MapPin, Plus, Calendar, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Spec = ({ label, value }) => (
  <div className="text-sm">
    <span className="text-gray-500">{label}: </span>
    <span className="font-medium text-gray-800">{value || '—'}</span>
  </div>
);

const ListingCard = ({ listing, onView, isAdmin, onAdminDelete }) => {
  const isTruck = listing.vehicle_category === 'truck';
  const title = isTruck
    ? (listing.truck_type === 'Other' ? listing.truck_type_other : listing.truck_type) || 'Truck'
    : listing.trailer_type || 'Trailer';
  return (
  <Card
    onClick={() => onView(listing.id)}
    className="p-4 cursor-pointer hover:shadow-md transition relative"
    data-testid={`trailer-listing-${listing.id}`}
  >
    {isAdmin && (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onAdminDelete(listing); }}
        className="absolute top-2 right-2 z-10 inline-flex items-center gap-1 bg-red-600 hover:bg-red-700 text-white text-[11px] font-semibold px-2 py-1 rounded-md shadow"
        data-testid={`admin-delete-trailer-${listing.id}`}
        title="Admin: delete this listing"
      >
        <Trash2 className="w-3.5 h-3.5" /> Admin Delete
      </button>
    )}
    {listing.photo_ids?.[0] && (
      <PhotoThumb fileId={listing.photo_ids[0]} className="w-full h-44 object-cover rounded mb-3" />
    )}
    <div className="flex items-center gap-2 mb-1">
      <span className={`text-[10px] tracking-wider font-bold uppercase px-2 py-0.5 rounded ${isTruck ? 'bg-amber-100 text-amber-800 border border-amber-300' : 'bg-blue-100 text-blue-800 border border-blue-300'}`}>
        {isTruck ? 'Truck' : 'Trailer'}
      </span>
    </div>
    <h3 className="font-bold text-[#1e3a5f] text-lg mb-1">{title}</h3>
    <p className="text-sm text-gray-600 mb-2 flex items-center gap-1">
      <MapPin className="w-3.5 h-3.5" /> {listing.base_location}
    </p>
    <div className="grid grid-cols-2 gap-x-2 gap-y-1 mb-3">
      <Spec label="Axles" value={listing.number_of_axles} />
      <Spec label="Payload" value={listing.average_payload} />
      {isTruck ? (
        <Spec label="Pallets" value={listing.pallet_spaces} />
      ) : (
        <Spec label="Deck" value={listing.deck_length} />
      )}
      <Spec label={isTruck ? 'GVM' : 'Coupling'} value={isTruck ? listing.gvm : listing.coupling_type} />
    </div>
    <Button size="sm" className="w-full bg-red-600 hover:bg-red-700">View &amp; Book</Button>
  </Card>
  );
};

const PhotoThumb = ({ fileId, className }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/trailer-hire/files/${fileId}`).then(res => {
      if (!cancelled) setSrc(res.data?.file_data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [fileId]);
  if (!src) return <div className={`${className} bg-gray-100 animate-pulse`} />;
  return <img src={src} alt="trailer" className={className} />;
};

const TrailerHire = () => {
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const userEmail = localStorage.getItem('user_email');
  const adminEmail = localStorage.getItem('adminEmail');
  const isAdmin = !!adminEmail;
  const userRoles = (() => {
    try { return JSON.parse(localStorage.getItem('user_roles') || '[]'); } catch { return []; }
  })();
  const canList = userRoles.includes('carrier') || userRoles.includes('admin') || userRoles.includes('crane_operator');

  const loadListings = async () => {
    try {
      const res = await axios.get(`${API}/trailer-hire/listings`);
      setListings(res.data || []);
    } catch {
      toast.error('Failed to load listings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadListings();
  }, []);

  const confirmAdminDelete = async () => {
    if (!deleteTarget || !adminEmail) return;
    try {
      setDeleting(true);
      await axios.delete(`${API}/trailer-hire/listings/${deleteTarget.id}`, {
        params: { operator_email: adminEmail },
      });
      toast.success('Listing removed');
      setDeleteTarget(null);
      // Optimistically drop from view, then refetch
      setListings((prev) => prev.filter((l) => l.id !== deleteTarget.id));
      loadListings();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="bg-[#1e3a5f] text-white py-10">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold flex items-center gap-2">
                <Truck className="w-8 h-8 text-red-300" />
                Truck &amp; Trailer Hire
              </h1>
              <p className="text-blue-100 mt-2 max-w-2xl">
                Hire trucks and trailers directly from operators across Australia. Free for paid subscribers to list — adhoc operators pay $49 only when a hire is confirmed.
              </p>
            </div>
            {canList && (
              <Button
                onClick={() => navigate('/list-trailer')}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="list-trailer-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> List My Truck or Trailer
              </Button>
            )}
            {!canList && userEmail && (
              <Link to="/signup" className="text-blue-100 underline text-sm">
                Sign up as a carrier to list a trailer
              </Link>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {loading ? (
          <Card className="p-12 text-center text-gray-500">Loading trailers…</Card>
        ) : listings.length === 0 ? (
          <Card className="p-12 text-center text-gray-500" data-testid="empty-listings">
            <Truck className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p>No trucks or trailers currently listed for hire.</p>
            {canList && (
              <Button
                onClick={() => navigate('/list-trailer')}
                className="mt-4 bg-[#1e3a5f] hover:bg-[#15304f] text-white"
              >
                Be the first to list
              </Button>
            )}
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {listings.map(l => (
              <ListingCard
                key={l.id}
                listing={l}
                onView={(id) => navigate(`/trailer-hire/${id}`)}
                isAdmin={isAdmin}
                onAdminDelete={(listing) => setDeleteTarget(listing)}
              />
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && !deleting && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Admin: Delete this listing?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the listing from the Truck &amp; Trailer Hire page immediately. The operator
              {deleteTarget?.operator_email ? ` (${deleteTarget.operator_email})` : ''} will no longer see it as active.
              Use this when the listing doesn't meet platform standards.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={confirmAdminDelete}
              disabled={deleting}
              data-testid="admin-delete-trailer-confirm"
            >
              {deleting ? 'Removing...' : 'Delete Listing'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Footer />
    </div>
  );
};

export default TrailerHire;
